import type { CellValue, Column } from "jspreadsheet-ce";

// 재무제표 본문 카테고리는 이 5종으로 고정이고, 나머지 카테고리는 주석 제목이다
// (예: "1. 일반사항"). 주석에만 문단이 있으므로 wrap 대상도 주석뿐이다.
const FINANCIAL_STATEMENT_CATEGORIES = new Set([
  "재무상태표",
  "손익계산서",
  "포괄손익계산서",
  "자본변동표",
  "현금흐름표",
]);

export const isNoteCategory = (category: string | undefined): boolean =>
  category != null && !FINANCIAL_STATEMENT_CATEGORIES.has(category);

const colLettersToIndex = (addr: string) => {
  const m = addr.match(/^([A-Za-z]+)/);
  if (!m) return null;
  const letters = m[1].toUpperCase();
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64); // A=1
  }
  return n - 1; // 0-based
};

const maxUsedColumnsOf = (data: CellValue[][]) => {
  let maxCols = 0;
  for (const row of data) {
    let lastIdx = -1;
    for (let j = row.length - 1; j >= 0; j--) {
      const v = row[j];
      const empty =
        v === null ||
        v === undefined ||
        (typeof v === "string" && v.trim() === "");
      if (!empty) {
        lastIdx = j;
        break;
      }
    }
    if (lastIdx + 1 > maxCols) maxCols = lastIdx + 1;
  }
  return maxCols;
};

const maxColumnFromMerges = (
  mergeCells: Record<string, [number, number]>,
) => {
  let maxCol = 0;
  for (const [addr, span] of Object.entries(mergeCells || {})) {
    const c = colLettersToIndex(addr);
    if (c == null) continue;
    const colspan = span?.[0] || 1;
    if (c + colspan > maxCol) maxCol = c + colspan;
  }
  return maxCol;
};

/**
 * 표 없이 문단만 있는 주석인지 판정한다. 표는 반드시 2열 이상을 차지하므로
 * "내용이 A열 하나뿐"이 곧 "표가 없다"가 된다. style 의 너비는 내용 없는 열에도
 * 남아 있어 판정에 쓰지 않는다.
 */
export const isParagraphOnlySheet = (
  data: CellValue[][],
  mergeCells: Record<string, [number, number]>,
): boolean => maxUsedColumnsOf(data) === 1 && maxColumnFromMerges(mergeCells) <= 1;

export function getColumns(
  data: CellValue[][],
  style: Record<string, string>,
  mergeCells: Record<string, [number, number]>,
) {
  const maxColsFromData = maxUsedColumnsOf(data);

  // Parse widths from style map and aggregate per column
  const colWidthVotes = new Map<number, Map<number, number>>();

  const widthRegex = /(?:^|;)\s*width\s*:\s*([\d.]+)\s*px\b/i;

  // Distribute merged width across its colspan
  for (const [addr, css] of Object.entries(style || {})) {
    const col = colLettersToIndex(addr);
    if (col == null) continue;

    const m = css.match(widthRegex);
    if (!m) continue;

    const px = Number(m[1]);
    if (!isFinite(px)) continue;

    const colspan = mergeCells?.[addr]?.[0] || 1;
    const perCol = px / Math.max(colspan, 1);

    for (let k = 0; k < colspan; k++) {
      const c = col + k;
      let votes = colWidthVotes.get(c);
      if (!votes) {
        votes = new Map();
        colWidthVotes.set(c, votes);
      }
      votes.set(perCol, (votes.get(perCol) || 0) + 1);
    }
  }

  // Choose the most common width per column (tie-break: larger width)
  const colWidth: Record<number, number> = {};
  for (const [c, votes] of colWidthVotes.entries()) {
    let best = 0;
    let bestCount = -1;
    for (const [w, count] of votes.entries()) {
      if (count > bestCount || (count === bestCount && w > best)) {
        best = w;
        bestCount = count;
      }
    }
    colWidth[c] = best;
  }

  // Ensure columns cover merges even if style had no width
  const maxColFromMerge = maxColumnFromMerges(mergeCells);

  const maxCols =
    Math.max(
      maxColsFromData,
      maxColFromMerge,
      ...Array.from(Object.keys(colWidth), (k) => Number(k) + 1),
    ) || 0;

  const LEFT_PADDING = 15;
  const FIRST_COLUMN_PADDING = 9;
  const TABLE_COLUMN_MAX_WIDTH = 440;
  const PARAGRAPH_ONLY_WIDTH = 600;

  // 표 없는 주석은 원본 너비를 그대로 쓰면 문단이 좁은 A열 안에서 세로로 길게
  // 감기므로, 읽을 수 있는 폭을 따로 준다.
  const isParagraphOnly = isParagraphOnlySheet(data, mergeCells);

  // A열도 옆 열을 참조하지 않고 자기 원본 폭을 쓴다. 예전 colWidth[1] * 2 규칙은
  // 원본과 무관한 고정 공식이라, DSD 원본 COLGROUP 폭과 평균 135.5px 어긋났다
  // (파라텍 21개 표 실측: 원본 100px 를 440px 로, 354px 를 142px 로 바꿔놓았다).
  // A열 하한은 두지 않는다 — 하한은 다시 원본에서 멀어지는 방향이고, 폭이 의미 없는
  // 구분자 열 표는 백엔드 MarkerListTable 이 style 을 버려 여기까지 오지 않는다.
  //
  // 패딩이 열마다 다른 이유: LEFT_PADDING 15px 은 합계 셀의 시그마 아이콘
  // (.cell-overlay-sum, left 7.2px + width 12px) 자리다. 합계는 금액 열에만 붙으므로
  // A열에는 그 몫이 필요 없고 들여쓰기 폭이 더 중요하다. 대신 DSD 가 COLGROUP 과 TD
  // 사이에 두는 9px 을 돌려줘 원본 열 폭을 복원한다 — 파라텍 21개 표에서 9px 은 13개가
  // 원본과 정확히 일치하고, 0px 과 15px 은 한 개도 맞지 않는다.
  const getWidth = (i: number): number | undefined => {
    if (isParagraphOnly && i === 0) return PARAGRAPH_ONLY_WIDTH;
    if (colWidth[i] == null) return undefined;
    const padding = i === 0 ? FIRST_COLUMN_PADDING : LEFT_PADDING;
    return Math.min(TABLE_COLUMN_MAX_WIDTH, colWidth[i] + padding);
  };

  const columns: Column[] = [];
  for (let i = 0; i < maxCols; i++) {
    const width = getWidth(i);
    columns.push({
      align: "left",
      type: "html",
      ...(width != null ? { width } : {}),
    });
  }

  return columns;
}
